import React from 'react';
import { cn } from '~/utils';
import { Icon } from './Icon';

export const Button = ({
  className,
  variant = 'primary',
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
}): React.ReactElement => (
  <button
    className={cn(
      'inline-flex min-h-11 items-center justify-center gap-2 rounded-xl px-4 text-sm font-semibold transition duration-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:pointer-events-none disabled:opacity-40 active:translate-y-px motion-reduce:transition-none',
      variant === 'primary' &&
        'bg-primary text-white shadow-[0_10px_30px_rgb(139_92_246_/_0.25)] hover:bg-primary-300 hover:text-black',
      variant === 'secondary' &&
        'border border-border bg-hover text-foreground hover:border-border hover:bg-chip',
      variant === 'ghost' && 'text-muted hover:bg-hover hover:text-foreground',
      variant === 'danger' &&
        'border border-rose-400/20 bg-rose-400/10 text-rose-200 hover:bg-rose-400/15',
      className
    )}
    {...props}
  />
);

const fieldClass =
  'min-h-11 w-full rounded-xl border border-border bg-input px-3.5 text-sm text-foreground outline-none transition placeholder:text-subtle hover:border-border focus:border-primary/60 focus:ring-2 focus:ring-primary/10 disabled:opacity-50';
export const TextField = (
  props: React.InputHTMLAttributes<HTMLInputElement>
): React.ReactElement => (
  <input {...props} className={cn(fieldClass, props.className)} />
);
export const TextArea = (
  props: React.TextareaHTMLAttributes<HTMLTextAreaElement>
): React.ReactElement => (
  <textarea
    {...props}
    className={cn(fieldClass, 'min-h-28 resize-none py-3', props.className)}
  />
);
export const Select = (
  props: React.SelectHTMLAttributes<HTMLSelectElement>
): React.ReactElement => (
  <select
    {...props}
    className={cn(
      fieldClass,
      'appearance-none bg-[linear-gradient(45deg,transparent_50%,#64748b_50%),linear-gradient(135deg,#64748b_50%,transparent_50%)] bg-[position:calc(100%-16px)_19px,calc(100%-11px)_19px] bg-[size:5px_5px,5px_5px] bg-no-repeat pr-9',
      props.className
    )}
  />
);
export const Label = ({
  children,
  htmlFor,
}: React.LabelHTMLAttributes<HTMLLabelElement>): React.ReactElement => (
  <label
    className="grid gap-2 text-xs font-semibold text-muted"
    htmlFor={htmlFor}
  >
    {children}
  </label>
);

export const Badge = ({
  children,
  tone = 'neutral',
}: React.PropsWithChildren<{
  tone?: 'neutral' | 'primary' | 'cyan' | 'amber' | 'green';
}>): React.ReactElement => (
  <span
    className={cn(
      'inline-flex items-center rounded-full border px-2 py-1 font-mono text-[10px] font-semibold uppercase tracking-[0.12em]',
      tone === 'neutral' && 'border-border bg-chip text-muted',
      (tone === 'primary' || tone === 'cyan') &&
        'border-primary/20 bg-primary/10 text-primary-200',
      tone === 'amber' && 'border-amber-300/20 bg-amber-300/10 text-amber-200',
      tone === 'green' &&
        'border-emerald-300/20 bg-emerald-300/10 text-emerald-200'
    )}
  >
    {children}
  </span>
);

export const Spinner = (): React.ReactElement => (
  <span
    aria-hidden="true"
    className="size-5 animate-spin rounded-full border-2 border-current border-r-transparent motion-reduce:animate-none"
  />
);

export const PageHeader = ({
  eyebrow,
  title,
  description,
  action,
}: {
  eyebrow: string;
  title: string;
  description?: string;
  action?: React.ReactNode;
}): React.ReactElement => (
  <header className="mb-8 flex flex-col gap-5 border-b border-border-subtle pb-7 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-primary-300">
        {eyebrow}
      </p>
      <h1 className="max-w-3xl text-3xl font-semibold tracking-[-0.04em] text-foreground sm:text-4xl">
        {title}
      </h1>
      {description && (
        <p className="mt-3 max-w-2xl text-sm leading-6 text-muted">
          {description}
        </p>
      )}
    </div>
    {action}
  </header>
);

export const EmptyState = ({
  title,
  description,
}: {
  title: string;
  description: string;
}): React.ReactElement => (
  <div className="grid min-h-52 place-items-center rounded-2xl border border-dashed border-border bg-chip p-8 text-center">
    <div>
      <div className="mx-auto mb-4 size-2 rounded-full bg-primary shadow-[0_0_24px_8px_rgb(139_92_246_/_0.25)]" />
      <h3 className="font-semibold text-foreground">{title}</h3>
      <p className="mt-2 text-sm text-muted">{description}</p>
    </div>
  </div>
);

export const SegmentedControl = ({
  value,
  options,
  onChange,
  label,
}: {
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  label: string;
}): React.ReactElement => (
  <div
    aria-label={label}
    className="inline-flex rounded-xl border border-border bg-input p-1"
    role="group"
  >
    {options.map(option => (
      <button
        className={cn(
          'min-h-9 rounded-lg px-3 text-xs font-semibold transition focus-visible:outline-2 focus-visible:outline-primary',
          value === option.value
            ? 'bg-hover text-foreground shadow-sm'
            : 'text-muted hover:text-foreground'
        )}
        key={option.value}
        onClick={() => onChange(option.value)}
        type="button"
      >
        {option.label}
      </button>
    ))}
  </div>
);

export const IconButton = ({
  label,
  icon,
  className,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement> & {
  label: string;
  icon: string;
}): React.ReactElement => (
  <button
    aria-label={label}
    className={cn(
      'grid size-11 place-items-center rounded-xl border border-border text-muted transition hover:bg-hover hover:text-foreground active:translate-y-px focus-visible:outline-2 focus-visible:outline-primary disabled:opacity-40',
      className
    )}
    type="button"
    {...props}
  >
    <Icon name={icon} />
  </button>
);

export const StatusBadge = ({
  status,
}: {
  status: string;
}): React.ReactElement => (
  <Badge
    tone={
      status === 'succeeded' || status === 'active'
        ? 'green'
        : status === 'failed'
          ? 'amber'
          : 'neutral'
    }
  >
    {status}
  </Badge>
);

export const Skeleton = ({
  className,
}: {
  className?: string;
}): React.ReactElement => (
  <div
    aria-hidden="true"
    className={cn(
      'animate-pulse rounded-xl bg-hover motion-reduce:animate-none',
      className
    )}
  />
);

export const Dialog = ({
  open,
  title,
  onClose,
  children,
}: React.PropsWithChildren<{
  open: boolean;
  title: string;
  onClose: () => void;
}>): React.ReactElement | null =>
  open ? (
    <div className="fixed inset-0 z-50 grid place-items-center bg-overlay p-4 backdrop-blur-sm">
      <section
        aria-labelledby="mylo-dialog-title"
        aria-modal="true"
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto overscroll-contain rounded-2xl border border-border bg-elevated p-6 shadow-2xl"
        role="dialog"
      >
        <div className="mb-5 flex items-center justify-between">
          <h2 className="font-semibold text-foreground" id="mylo-dialog-title">
            {title}
          </h2>
          <IconButton icon="close" label="Close" onClick={onClose} />
        </div>
        {children}
      </section>
    </div>
  ) : null;

export const Drawer = ({
  open,
  label,
  children,
}: React.PropsWithChildren<{
  open: boolean;
  label: string;
}>): React.ReactElement => (
  <aside
    aria-hidden={!open}
    aria-label={label}
    className={cn(
      'fixed inset-y-0 right-0 z-50 w-full max-w-md overscroll-contain border-l border-border bg-elevated p-6 shadow-2xl transition-transform',
      open ? 'translate-x-0' : 'translate-x-full'
    )}
  >
    {children}
  </aside>
);

export const Popover = ({
  open,
  children,
}: React.PropsWithChildren<{ open: boolean }>): React.ReactElement | null =>
  open ? (
    <div className="absolute z-40 min-w-48 rounded-xl border border-border bg-elevated p-2 shadow-2xl">
      {children}
    </div>
  ) : null;

export const Tabs = SegmentedControl;

export const MediaCard = ({
  title,
  meta,
  className,
}: {
  title: string;
  meta: string;
  className?: string;
}): React.ReactElement => (
  <article
    className={cn('rounded-2xl border border-border bg-card p-3', className)}
  >
    <div className="aspect-video rounded-xl bg-gradient-to-br from-primary via-violet-800 to-slate-950" />
    <h3 className="mt-3 text-sm font-semibold text-foreground">{title}</h3>
    <p className="mt-1 text-xs text-muted">{meta}</p>
  </article>
);

export const ToolCard = MediaCard;
export const ModelPicker = Select;

export const UploadZone = ({
  label,
  onChange,
}: {
  label: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
}): React.ReactElement => (
  <label className="grid min-h-32 cursor-pointer place-items-center rounded-xl border border-dashed border-border bg-chip text-sm text-muted transition hover:border-primary/40 hover:text-primary-text focus-within:outline-2 focus-within:outline-primary">
    <input className="sr-only" onChange={onChange} type="file" />
    {label}
  </label>
);
